import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Depot } from '@interfaces/depot.interface';
import { DepotService } from 'src/app/locations/services/depot.service';
import { DriverDepositReportService, DriverDepositReturnsParams } from '../../services/driver-deposit-report.service';
import { ToastrProviderService } from '@services/toastr-provider.service';
import { AngularMultiSelectOptions } from '@shared/constants/multiselect-dropdown.constants';
import { DateRange } from '@enums/enum';

/**
 * Interface for table column configuration
 * @interface TableColumn
 */
interface TableColumn {
    prop: string;
    name: string;
    flexGrow: number;
}

/**
 * Interface for dropdown list items
 * @interface DropdownItem
 */
interface DropdownItem {
    id: number;
    itemName: string;
}

/**
 * Component for displaying and exporting driver deposit returns report
 * Allows filtering by date range, depot, report type, and return collection status
 * Displays preview of first 10 records and exports full CSV report
 * 
 * @export
 * @class DriverDepositReturnsReportComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'app-driver-deposit-returns-report',
    templateUrl: './driver-deposit-returns-report.component.html',
    styleUrls: ['./driver-deposit-returns-report.component.scss']
})
export class DriverDepositReturnsReportComponent implements OnInit, OnDestroy {
    /** Observable stream of available depots for the user */
    public depots$: Observable<Depot[]>;
    
    /** Loading state indicator for async operations */
    public loading: boolean = false;
    
    /** Currently selected date range enum value */
    public dateRange: DateRange = DateRange.LAST_7_DAYS;
    
    /** Parsed report data displayed in the table (first 10 rows) */
    public reportData: any[] = [];
    
    /** Flag indicating if report data is available for display */
    public hasData: boolean = false;
    
    /** Current locale for translations */
    public locale: string;
    
    /** Query parameters for report generation */
    public queryParams: DriverDepositReturnsParams = {
        startDate: '',
        endDate: '',
        reportType: 'SUMMARY',
        returnCollected: null,
        depots: [],
        driverId: undefined
    };

    /** List of all available depots for dropdown */
    public dropdownList: DropdownItem[] = [];
    
    /** Currently selected depots in multiselect */
    public selectedDepots: DropdownItem[] = [];
    
    /** Configuration settings for multiselect dropdown */
    public dropdownSettings = AngularMultiSelectOptions;

    /** Table column definitions */
    public columns: TableColumn[] = [
        { prop: 'date', name: this.translate.instant('Date'), flexGrow: 1 },
        { prop: 'routeNumber', name: this.translate.instant('Route number'), flexGrow: 1 },
        { prop: 'stepNo', name: this.translate.instant('Step No'), flexGrow: 1 },
        { prop: 'orderId', name: this.translate.instant('Order Id'), flexGrow: 1.5 },
        { prop: 'driver', name: this.translate.instant('Driver'), flexGrow: 2 },
        { prop: 'returnCollected', name: this.translate.instant('Return Collected'), flexGrow: 1 },
        { prop: 'bagRef', name: this.translate.instant('Bag Reference'), flexGrow: 1.5 }
    ];

    /** Subject for managing component subscriptions lifecycle */
    private readonly destroy$ = new Subject<void>();

    /** Maximum allowed date range in days for report generation */
    private readonly MAX_DATE_RANGE_DAYS = 365;

    /** Number of rows to display in preview table */
    private readonly PREVIEW_ROWS_LIMIT = 10;

    /**
     * Creates an instance of DriverDepositReturnsReportComponent
     * @param {DriverDepositReportService} driverDepositReportService - Service for fetching driver deposit reports
     * @param {DepotService} depotService - Service for fetching depot data
     * @param {TranslateService} translate - Service for handling translations
     * @param {ToastrProviderService} toastrProviderService - Service for displaying toast notifications
     */
    constructor(
        private readonly driverDepositReportService: DriverDepositReportService,
        private readonly depotService: DepotService,
        private readonly translate: TranslateService,
        private readonly toastrProviderService: ToastrProviderService
    ) {}

    /**
     * Angular lifecycle hook - component initialization
     * Sets up locale, loads depots, and subscribes to language changes
     * 
     * @memberof DriverDepositReturnsReportComponent
     */
    public ngOnInit(): void {
        this.locale = this.translate.currentLang;
        this.loadDepots();
        this.subscribeToLanguageChanges();
    }

    /**
     * Angular lifecycle hook - component cleanup
     * Unsubscribes from all active subscriptions
     * 
     * @memberof DriverDepositReturnsReportComponent
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Loads available depots for the current user
     * Initializes dropdown list values upon successful load
     * 
     * @private
     * @memberof DriverDepositReturnsReportComponent
     */
    private loadDepots(): void {
        this.depots$ = this.depotService.getDepotsForUser().pipe(
            tap((depots: Depot[]) => this.initDropdownListValues(depots)),
            takeUntil(this.destroy$)
        );
    }

    /**
     * Subscribes to language change events
     * Updates locale and column names when language changes
     * 
     * @private
     * @memberof DriverDepositReturnsReportComponent
     */
    private subscribeToLanguageChanges(): void {
        this.translate.onLangChange
            .pipe(takeUntil(this.destroy$))
            .subscribe((event: LangChangeEvent) => {
                this.locale = event.lang;
                this.updateColumnNames();
            });
    }

    /**
     * Updates table column names with current translations
     * Called when language changes
     * 
     * @private
     * @memberof DriverDepositReturnsReportComponent
     */
    private updateColumnNames(): void {
        this.columns = [
            { prop: 'date', name: this.translate.instant('Date'), flexGrow: 1 },
            { prop: 'routeNumber', name: this.translate.instant('Route number'), flexGrow: 1 },
            { prop: 'stepNo', name: this.translate.instant('Step No'), flexGrow: 1 },
            { prop: 'orderId', name: this.translate.instant('Order Id'), flexGrow: 1.5 },
            { prop: 'driver', name: this.translate.instant('Driver'), flexGrow: 2 },
            { prop: 'returnCollected', name: this.translate.instant('Return Collected'), flexGrow: 1 },
            { prop: 'bagRef', name: this.translate.instant('Bag Reference'), flexGrow: 1.5 }
        ];
    }

    /**
     * Handles date range change event from date picker
     * Updates query parameters with formatted dates
     * 
     * @param {*} event - Date range change event containing dateStart, dateEnd, and dateRange
     * @memberof DriverDepositReturnsReportComponent
     */
    public changedDateRange(event: any): void {
        this.queryParams.startDate = moment(event.dateStart).format('YYYY-MM-DD');
        this.queryParams.endDate = moment(event.dateEnd).format('YYYY-MM-DD');
        this.dateRange = event.dateRange;
    }

    /**
     * Initializes dropdown list values from depot data
     * Selects first depot by default if available
     * 
     * @param {Depot[]} depots - Array of available depots
     * @memberof DriverDepositReturnsReportComponent
     */
    public initDropdownListValues(depots: Depot[]): void {
        this.dropdownList = depots.map((depot: Depot) => ({
            id: depot.id,
            itemName: depot.code
        }));
        
        if (this.dropdownList.length > 0) {
            this.selectedDepots = [this.dropdownList[0]];
        }
    }

    /**
     * Exports driver deposit returns report
     * Validates date range, generates CSV, displays preview, and triggers download
     * Shows appropriate error/success notifications
     * 
     * @memberof DriverDepositReturnsReportComponent
     */
    public exportReport(): void {
        if (!this.validateDateRange()) {
            return;
        }

        this.loading = true;
        this.prepareDepotParams();

        this.driverDepositReportService
            .exportDriverDepositReturnsCsv(this.queryParams)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (blob: Blob) => {
                    this.handleReportSuccess(blob);
                },
                (error) => {
                    this.handleReportError(error);
                }
            );
    }

    /**
     * Validates selected date range
     * Checks if dates are selected and within maximum allowed range
     * 
     * @private
     * @returns {boolean} True if date range is valid, false otherwise
     * @memberof DriverDepositReturnsReportComponent
     */
    private validateDateRange(): boolean {
        if (!this.queryParams.startDate || !this.queryParams.endDate) {
            this.toastrProviderService.showWarning(
                this.translate.instant('Please select a date range')
            );
            return false;
        }

        const days = moment(this.queryParams.endDate).diff(this.queryParams.startDate, 'days');
        if (days > this.MAX_DATE_RANGE_DAYS) {
            this.toastrProviderService.showWarning(
                this.translate.instant('The date range selected is too large The maximum date range is 365 days')
            );
            return false;
        }

        return true;
    }

    /**
     * Prepares depot parameters from selected depots for query
     * Maps selected depot objects to array of depot IDs
     * 
     * @private
     * @memberof DriverDepositReturnsReportComponent
     */
    private prepareDepotParams(): void {
        this.queryParams.depots = this.selectedDepots.length > 0
            ? this.selectedDepots.map((depot) => depot.id)
            : [];
    }

    /**
     * Handles successful report generation
     * Parses CSV for preview and triggers file download
     * 
     * @private
     * @param {Blob} blob - CSV file blob
     * @memberof DriverDepositReturnsReportComponent
     */
    private handleReportSuccess(blob: Blob): void {
        this.parseCsvAndDisplayData(blob);
        this.downloadCsvFile(blob);
        
        this.loading = false;
        this.toastrProviderService.showSuccess(
            this.translate.instant('Report downloaded successfully')
        );
    }

    /**
     * Handles report generation error
     * Displays error notification to user
     * 
     * @private
     * @param {*} error - Error object from failed request
     * @memberof DriverDepositReturnsReportComponent
     */
    private handleReportError(error: any): void {
        this.loading = false;
        this.toastrProviderService.showError(
            this.translate.instant('Error downloading report')
        );
    }

    /**
     * Downloads CSV file to user's device
     * Creates temporary download link and triggers download
     * 
     * @private
     * @param {Blob} blob - CSV file blob
     * @memberof DriverDepositReturnsReportComponent
     */
    private downloadCsvFile(blob: Blob): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `driver-deposit-returns-${this.queryParams.startDate}-${this.queryParams.endDate}.csv`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Parses CSV blob and displays first rows in preview table
     * Reads CSV content, parses rows handling quoted values, and populates reportData
     * 
     * @private
     * @param {Blob} blob - CSV file blob to parse
     * @memberof DriverDepositReturnsReportComponent
     */
    private parseCsvAndDisplayData(blob: Blob): void {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const csvText = e.target.result;
            const rows = csvText.split('\n').filter(row => row.trim() !== '');
            
            if (rows.length <= 1) {
                this.reportData = [];
                this.hasData = false;
                return;
            }

            const headers = this.parseCsvRow(rows[0]);
            const dataRows = rows.slice(1, this.PREVIEW_ROWS_LIMIT + 1);
            
            this.reportData = dataRows.map(row => this.mapCsvRowToObject(row, headers));
            this.hasData = this.reportData.length > 0;
        };
        
        reader.readAsText(blob);
    }

    /**
     * Parses a single CSV row handling quoted values and commas
     * Properly handles commas within quoted strings
     * 
     * @private
     * @param {string} row - CSV row string to parse
     * @returns {string[]} Array of parsed cell values
     * @memberof DriverDepositReturnsReportComponent
     */
    private parseCsvRow(row: string): string[] {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < row.length; i++) {
            const char = row[i];
            
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current.trim());
        return result;
    }

    /**
     * Maps parsed CSV row values to object using headers as keys
     * 
     * @private
     * @param {string} row - CSV row string
     * @param {string[]} headers - Array of header names to use as object keys
     * @returns {*} Object with properties mapped from CSV row
     * @memberof DriverDepositReturnsReportComponent
     */
    private mapCsvRowToObject(row: string, headers: string[]): any {
        const values = this.parseCsvRow(row);
        const obj: any = {};
        
        headers.forEach((header, index) => {
            obj[header] = values[index] || '';
        });
        
        return obj;
    }
}

